// noinspection JSUnusedGlobalSymbols

import axios, {
	AxiosError
} from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { Banner } from '@/models/utils/Toaster.js'
import { useBroadcastChannel } from '@vueuse/core'
import { Util } from '@/models/utils/Util.js'
import { echoes } from '@/composables/echo/useEchoes.js'

export default class ApiGateway
{
	axiosInstance = null
	channel = useBroadcastChannel({ name: 'ApiGateway' })

	constructor()
	{
		const defaultConfig = {
			baseURL: import.meta.env.VITE_API_URL,
			headers: {
				'Content-Type': 'application/json',
				'x-client-request-id': this.guid()
			}
		}
		this.axiosInstance = axios.create(defaultConfig)

	}

	/**
	 * Creates a guid so we can trace a request from the client
	 * @param [a]
	 * @returns {string|*}
	 */
	guid(a)
	{
		return a?(a^Math.random()*16>>a/4).toString(16):([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,this.guid)
	}

	async axiosCall(config)
	{
		const authStore = useAuthStore()
		if (authStore.isLoggedIn)
		{
			if (!config)
			{
				config = {}
			}
			if (!config.headers)
			{
				config.headers = {}
			}
			config.headers['Authorization'] = `Bearer ${authStore.rawJwt}`

			try
			{
				//Used for error recording and tracking only. Not used for getting data as it can be manipulated.
				config.headers['x-client-user-id'] = authStore.getAuth?.user()?.id
			}
			catch(e){
				config.headers['x-client-user-id'] = 0
			}

			try
			{
				//Required when not using a global Axios instance. This is used to allow "->toOthers()" to work on broadcasts.
				config.headers['X-Socket-ID'] = echoes.socketId();
			}
			catch(e){
				//None
			}

			try
			{
				// Attach the JWT version which is verified by the gateway
				config.headers['x-jwt-version-code'] = authStore.getAuth?.user()?.jwt_version_code
			}
			catch(e) {/**/}
		}
		try
		{
			const response = await this.axiosInstance.request(config)
			return new ApiGatewayResponse(null, response)
		}
		catch (error)
		{
			if (error instanceof AxiosError) {
				if (error.status === HttpStatusCode.ServiceUnavailable) {
					// User is currently logged in, so schedule them to be logged out
					if (useAuthStore().isLoggedIn) {
						useAuthStore().unAuthenticated = true
					}
				}
				return new ApiGatewayResponse(error, null)
			}
			else {
				return new ApiGatewayResponse(null, null)
			}
		}
	}

	async get(route, params)
	{
		//Let session timeout know request occurred
		await this.channel.post({ method: 'get' })
		return this.axiosCall({ method: 'get', url: route, params: params })
	}

	/**
	 * Special function that does not reset timeout modal which would cause infinite loop when it
	 * updates the user session.
	 * @param route
	 * @param params
	 * @returns {Promise<ApiGatewayResponse|undefined>}
	 */
	async get_for_reset(route, params)
	{
		//Don't send message to session timeout modal here
		return this.axiosCall({ method: 'get', url: route, params: params })
	}

	async post(route, data, params)
	{
		//Let session timeout know request occurred
		await this.channel.post({ method: 'post' })
		return this.axiosCall({ method: 'post', url: route, params: params, data: data, timeout: 66000 })
	}

	async patch(route, data, params)
	{
		//Let session timeout know request occurred
		await this.channel.post({ method: 'patch' })
		return this.axiosCall({ method: 'patch', url: route, params: params, data: data })
	}

	async delete(route, params)
	{
		//Let session timeout know request occurred
		await this.channel.post({ method: 'delete' })
		return this.axiosCall({ method: 'delete', url: route, params: params })
	}
}

export class ApiGatewayParams
{
	/**
	 * Example
	 *  filters: { 'given_name': 'TestName' }
	 *  filters: { 'given_name': {'prefix': 'Test'} }
	 *  filters: { 'given_name': {'in': ['TestName1', 'TestName2'] } }
	 *  filters: { 'given_name': {'fulltext_search': 'Test'} }
	 */
	filters = null
	/**
	 * Example
	 *  fields: '*'
	 *  fields: 'given_name,family_name'
	 *  fields: {'user': 'given_name,family_name'}
	 */
	fields = null
	/**
	 * Example
	 *  cursor: 1
	 *  cursor: { 'user': 1 }
	 *  cursor: { 'user': 1, 'permission': 3 }
	 */
	cursor = 0
	/**
	 * Example
	 *  limit: 20
	 *  limit: { 'user': 20 }
	 *  limit: { 'user': 20, 'permission': 40 }
	 */
	limit = 0
	/**
	 * Example
	 *  page: 1
	 *  page: 2
	 */
	page = 0
	/**
	 * Example
	 *  sort: 'family_name'
	 *  sort: '-family_name,given_name'
	 *  sort: { 'user': '-family_name,given_name' }
	 *  sort: { 'user': 'family_name,-given_name', 'permission': 'organization_id' }
	 */
	sort = null
	/**
	 * Example
	 *  relationships: 'permission'
	 *  relationships: {'user': 'permission', 'permission': 'anotherModel'}
	 */
	relationships = null
}

export class ApiGatewayResponse
{
	error = null
	response = null
	_overrideError = null

	/**
	 * Banner object to show messaging to users
	 * @type {Banner}
	 */
	banner = null

	constructor(error, response)
	{
		this.error = error
		this.response = response
		this.banner = new Banner()
	}

	/**
	 * Checks if an error was returned by the api-gateway service.
	 * @param {boolean} [p_computed_eh] Set true if being called in a computed function to prevent repeated calls
	 * @returns {boolean} True if an error has been set
	 */
	hasError(p_computed_eh)
	{
		if (this.error)
		{
			if (!p_computed_eh)
			{
				//Check if response is due to loss of authentication, if so show timeout alert and logout user
				const authStore = useAuthStore()
				authStore.handleResponseAuthError(this)
			}
		}

		return !!(this.error || this.response === null || this._overrideError)
	}

	/**
	 * Gets the data from the response or returns null
	 * @returns {Object|null}
	 */
	getData()
	{
		if (Util.hasProp(this.response, 'data') && Util.hasProp(this.response.data, 'data'))
		{
			return this.response.data.data;
		}
		else if (Util.hasProp(this.response, 'data'))
		{
			return this.response.data;
		}
		return null;
	}

	status()
	{
		return this.error?.status ?? this.response?.status ?? 500
	}

	body()
	{
		return this.response?.data
	}

	overrideError(error)
	{
		if (!error.status)
		{
			error.status = HttpStatusCode.InternalServerError
		}
		if (!error.title)
		{
			error.title = 'Unknown Error'
		}
		if (!error.detail)
		{
			error.detail = 'An unknown error was encountered.'
		}
		this._overrideError = error
	}

	/**
	 * Creates an array of error messages.
	 * Includes both the error title (generic message) and error details (indented in a div if title is present).
	 * If development, error detail are shown, otherwise hidden;
	 * @returns {string[]}
	 */
	errorMessages()
	{
		const rawErrors = this.error?.response?.data?.errors ?? []
		if (this._overrideError)
		{
			rawErrors.push(this._overrideError)
		}
		const errors = []
		for (const i in rawErrors)
		{
			const error = rawErrors[i]
			let has_title = false;
			if (Object.prototype.hasOwnProperty.call(error, 'title'))
			{
				errors.push(error['title'])
				has_title = true;
			}

			if (Object.prototype.hasOwnProperty.call(error, 'detail'))
			{
				if (has_title)
				{
					let hidden_class = ""
					if (import.meta.env.VITE_ENV !== 'dev')
					{
						hidden_class += "hidden-error"
					}
					errors.push('<div class="' + hidden_class +'" style="margin-left:10px;font-size:11px;">' + error['detail'] + "</div>")
				}
				else
				{
					errors.push(error['detail'])
				}
			}
		}
		if (errors.length <= 0)
		{
			errors.push('An unknown error was encountered.')
		}
		return errors
	}

	/**
	 * Gets all the detail messages from an error response.
	 * @returns {string[]} Array of messages
	 */
	errorDetails()
	{
		const rawErrors = this.error?.response?.data?.errors ?? []
		if (this._overrideError)
		{
			rawErrors.push(this._overrideError)
		}
		const errors = []
		for (const i in rawErrors)
		{
			const error = rawErrors[i]
			if (Object.prototype.hasOwnProperty.call(error, 'detail'))
			{
				errors.push(error['detail'])
			}
		}
		if (errors.length <= 0)
		{
			errors.push('An unknown error was encountered.')
		}
		return errors
	}

	/**
	 * Gets all the title messages from an error response. These are the more generic error messages.
	 * @returns {string[]} Array of messages
	 */
	errorTitles()
	{
		const rawErrors = this.error?.response?.data?.errors ?? []
		if (this._overrideError)
		{
			rawErrors.push(this._overrideError)
		}
		const errors = []
		for (const i in rawErrors)
		{
			const error = rawErrors[i]
			if (Object.prototype.hasOwnProperty.call(error, 'title'))
			{
				errors.push(error['title'])
			}
		}
		if (errors.length <= 0)
		{
			errors.push('An unknown error was encountered.')
		}
		return errors
	}

	validationErrors()
	{
		return this.errorsOfStatus(HttpStatusCode.ValidationError)
	}

	forbiddenErrors()
	{
		return this.errorsOfStatus(HttpStatusCode.Forbidden)
	}

	errorsOfStatus(statusCode)
	{
		const errorsOfStatus = []
		if (this.status() === statusCode)
		{
			const rawErrors = this.error?.response?.data?.errors ?? []
			if (this._overrideError)
			{
				rawErrors.push(this._overrideError)
			}
			for (const i in rawErrors)
			{
				const error = rawErrors[i]
				if (error.status === statusCode)
				{
					errorsOfStatus.push(error)
				}
			}
		}
		return errorsOfStatus
	}

	errorsNotOfStatus(statusCode)
	{
		const errorsNotOfStatus = []
		const rawErrors = this.error?.response?.data?.errors ?? []
		if (this._overrideError)
		{
			rawErrors.push(this._overrideError)
		}
		for (const i in rawErrors)
		{
			const error = rawErrors[i]
			if (error.status !== statusCode)
			{
				errorsNotOfStatus.push(error)
			}
		}
		return errorsNotOfStatus
	}

	/**
	 * Show an error Banner if an error is returned by a response.
	 * Banner is only shown if there are no validation errors.
	 * Error detail included only for development
	 * @param {string | string[] | null} [p_message] Message to show instead of the error titles.
	 * @returns {ApiGatewayResponse|null}
	 */
	showErrorBanner(p_message)
	{
		//Only show the banner if there are no validation errors
		if (this.validationErrors().length === 0)
		{
			let banner_id = ""
			if (this.forbiddenErrors().length > 0)
			{
				banner_id = "unauthorized"
			}

			if (p_message && p_message.length > 0)
			{
				this.banner.error(p_message, banner_id)
			}
			else
			{
				let error_text;
				if (import.meta.env.VITE_ENV === 'dev')
				{
					error_text = this.errorMessages();
				}
				else
				{
					error_text = this.errorTitles()

					if (error_text.length === 0)
					{
						error_text.push("An unknown error was encountered.")
					}
				}

				//This is a cludge and should be improved but release next week!
				let inactive_tenant = false;
				for (let i = 0; i < error_text.length; i++)
				{
					if (error_text[i].indexOf('Tenant is not active.') > -1)
					{
						inactive_tenant = true;
					}
				}
				if (inactive_tenant)
				{
					this.banner.info('Website is not active or undergoing maintenance.')
				}
				else
				{
					this.banner.error(error_text, banner_id)
				}
			}

			//Only show the error details in the input for dev
			if (import.meta.env.VITE_ENV === 'dev')
			{
				return this;
			}
		}
		else
		{
			//Needed for validation errors to show.
			return this;
		}

		//If it wasn't a validation error and this is prod, return null.
		return null;
	}
}

export class ApiGatewayException
{
	errors = null
	meta = null
}

export class ApiGatewayError
{
	title = null
	status = null
	detail = null
	source = null
}

export class HttpStatusCode
{
	static Unauthorized = 401
	static Forbidden = 403
	static NotFound = 404
	static ValidationError = 422
	static InternalServerError = 500
	static ServiceUnavailable = 503
}

export const api = new ApiGateway()
